// Bilder des Radios: hochladen, beschreiben, auswählen, löschen.
//
// Jedes Bild braucht eine kurze Beschreibung. Menschen, die nicht sehen können,
// bekommen sie vorgelesen – deshalb ist sie Pflicht und nicht nur ein Vorschlag.

import { useCallback, useEffect, useRef, useState } from "react";
import { siteApi, type MediaItem } from "../api";

/**
 * In welchem Kanalbereich gerade hochgeladen wird.
 *
 * Wer Seitenrechte nur über eine Kanalrolle hat, darf Bilder nur für diesen
 * Kanal hochladen (die API prüft das an `?channel_id=`). Die Bildfelder sitzen
 * tief in den Baustein-Editoren; statt den Kanal durch alle zu reichen, meldet
 * die Seite ihren Bereich hier an – solange sie offen ist.
 */
let hochladenFuerKanal: string | null = null;

/** Die Seite meldet ihren Kanalbereich an (null = Radio). Beim Verlassen zurück auf Radio. */
export function useMedienKanal(channelId: string | null): void {
  useEffect(() => {
    hochladenFuerKanal = channelId;
    return () => { hochladenFuerKanal = null; };
  }, [channelId]);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface MediaState {
  items: MediaItem[];
  used: number;
  quota: number;
}

export function useMediaLibrary(): {
  state: MediaState;
  error: string | null;
  busy: boolean;
  reload: () => Promise<void>;
  upload: (file: File, alt: string) => Promise<MediaItem | null>;
  rename: (id: string, alt: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
} {
  const [state, setState] = useState<MediaState>({ items: [], used: 0, quota: 0 });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await siteApi.media();
      setState({ items: res.items, used: res.used_bytes, quota: res.quota_bytes });
    } catch {
      setError("Die Bilder ließen sich nicht laden.");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const upload = useCallback(async (file: File, alt: string): Promise<MediaItem | null> => {
    setBusy(true);
    setError(null);
    try {
      const item = await siteApi.uploadImage(file, alt, hochladenFuerKanal);
      await reload();
      return item;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Das Bild ließ sich nicht hochladen.");
      return null;
    } finally {
      setBusy(false);
    }
  }, [reload]);

  const rename = useCallback(async (id: string, alt: string) => {
    await siteApi.updateImage(id, alt);
    await reload();
  }, [reload]);

  const remove = useCallback(async (id: string) => {
    await siteApi.deleteImage(id);
    await reload();
  }, [reload]);

  return { state, error, busy, reload, upload, rename, remove };
}

export function MediaPicker({ onPick, onClose, publicUrl }: {
  onPick: (item: MediaItem) => void;
  onClose: () => void;
  publicUrl: string;
}): JSX.Element {
  const { state, error, busy, upload } = useMediaLibrary();
  const [alt, setAlt] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const doUpload = async (): Promise<void> => {
    if (!file || alt.trim() === "") return;
    const item = await upload(file, alt.trim());
    if (item) {
      setFile(null);
      setAlt("");
      if (fileRef.current) fileRef.current.value = "";
      onPick(item);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Bild auswählen">
      <div className="card mt-8 w-full max-w-3xl animate-fade-in overflow-hidden shadow-lg">
        <div className="card-header">
          <h2 className="text-base font-semibold text-primary">Bild auswählen</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm">Schließen</button>
        </div>

        <div className="p-5">
        <div className="well">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-sm font-medium text-primary">Neues Bild hochladen</div>
            <span className="text-xs tabular-nums text-muted">Belegt: {formatBytes(state.used)} von {formatBytes(state.quota)}.</span>
          </div>
          {/* Belegung als schmaler Balken */}
          {state.quota > 0 && (
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-card">
              <div className="h-full rounded-full bg-brand-primary" style={{ width: `${Math.min(100, Math.round((state.used / state.quota) * 100))}%` }} />
            </div>
          )}
          <p className="mt-2 text-xs text-muted">
            Bilder werden automatisch verkleinert und in ein sparsames Format umgewandelt.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="min-w-0 text-sm text-secondary file:mr-3 file:rounded-md file:border file:border-solid file:border-line-strong file:bg-card file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary" aria-label="Bilddatei" />
            <input value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={200}
              placeholder="Was ist auf dem Bild zu sehen?" aria-label="Bildbeschreibung"
              className="input" />
            <button onClick={() => void doUpload()} disabled={!file || alt.trim() === "" || busy}
              className="btn btn-primary">
              {busy ? "Lädt …" : "Hochladen"}
            </button>
          </div>
          {error && <div className="callout callout-danger mt-3">{error}</div>}
        </div>

        {state.items.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-line-strong p-6 text-center text-sm text-muted">Noch keine Bilder da.</p>
        ) : (
          <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {state.items.map((item) => (
              <li key={item.id}>
                <button onClick={() => onPick(item)} className="group w-full overflow-hidden rounded-lg border border-line bg-card text-left shadow-sm transition hover:border-brand-primary hover:shadow-md">
                  <img src={`${publicUrl}${item.url}`} alt={item.alt} className="h-24 w-full bg-input object-cover transition group-hover:scale-[1.03]" loading="lazy" />
                  <span className="block truncate px-2 py-1.5 text-xs text-muted" title={item.alt}>{item.alt}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>
      </div>
    </div>
  );
}
